import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { BsPlusCircle } from "react-icons/bs";
import { GoTriangleDown } from "react-icons/go";
import Select, { components } from "react-select";
import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";
import { WarningIcon, VehicleIcon, CloseIcon } from "../../../../components/icons/index";

const VehicleSelectionSection = forwardRef(({
  vehicles,
  isVehicleLoading,
  handleRemoveVehicle,
  handleChangeVehicle,
  formatVehicle,
  vehicleOptions,
  renderVehicleCard,
  handleAddVehicle,
  isInvalid = { isInvalid: false, type: null },
}, ref) => {
  const { t, i18n } = useTranslation();
  const getTypographyClass = createTypographyHelper(i18n.language);
  const isInvalidState = isInvalid?.isInvalid || false;
  const invalidType = isInvalid?.type || null;

  return (
    <section ref={ref} className="tw:!mb-4 tw:!bg-white">
      <div className="tw:!bg-[#282828] tw:!rounded-t-[5px] tw:!px-5 tw:!py-3.5">
        <p className="tw:!mb-0 tw:!text-[#FFFFFF] tw:!text-[16px] tw:!font-[500]">
          {t("editBooking.BookingEditPage.vehicleSelection.title")}
        </p>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6 tw:border tw:border-[#e8e8e8] tw:rounded-b-[5px]">
        {isVehicleLoading ? (
          <div className="tw:flex tw:items-center tw:gap-2 tw:text-[#8d8d8d]">
            <div className="tw:h-4 tw:w-4 tw:rounded-full tw:border-2 tw:border-[#d1d1d1] tw:border-t-[#282828] tw:animate-spin" />
            <span className={getTypographyClass(13)}>{t("editBooking.BookingEditPage.vehicleSelection.loading")}</span>
          </div>
        ) : (
          <>
            {
              isInvalidState && (
                <div className="tw:flex tw:gap-4 tw:!mb-2">
                  <div className="tw:flex tw:w-full tw:gap-3 tw:!items-center">
                    <div className="tw:w-[44px] tw:h-[44px] tw:rounded-full tw:bg-[#FFEAE7] tw:grid tw:place-items-center tw:flex-shrink-0">
                      <WarningIcon width={19} height={16} color="#C01F12" />
                    </div>
                    <div className="tw:w-full">
                      {invalidType === "unselected" ? (
                        <p className={`tw:!text-[#C01F12] tw:!mb-0 ${getTypographyClass(13)}`}>
                          {t("editBooking.BookingEditPage.vehicleSelection.invalid.unselected")}
                        </p>
                      ) : invalidType === "capacity" ? (
                        <>
                          <p className={`tw:!text-[#C01F12] tw:!mb-0 ${getTypographyClass(13)}`}>
                            {t("editBooking.BookingEditPage.vehicleSelection.invalid.message")}
                          </p>
                          <p className={`tw:!text-[#C01F12] tw:!mb-0 ${getTypographyClass(13)}`}
                            dangerouslySetInnerHTML={{ __html: t("editBooking.BookingEditPage.vehicleSelection.invalid.title") }}
                          />
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            }

            <div className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-3 tw:gap-4">
              {vehicles.map((vehicle, index) => (
                <div key={vehicle.id} className="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                  <div className="tw:flex tw:flex-col tw:w-full">
                    <div className="tw:mb-2 tw:flex tw:items-center tw:justify-between">
                      <div className={`tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                        {t("editBooking.BookingEditPage.vehicleSelection.vehicle", { number: index + 1 })}<span className="tw:text-[#ea4335]">*</span>
                      </div>
                      {vehicles.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveVehicle(vehicle.id)}
                          disabled={vehicles.length <= 1}
                          className={cn(
                            "tw:w-6 tw:h-6 tw:rounded-full tw:border tw:flex tw:items-center tw:justify-center tw:text-[16px] tw:leading-none",
                            vehicles.length <= 1
                              ? "tw:border-[#e8e8e8] tw:text-[#d1d1d1] tw:cursor-not-allowed"
                              : "tw:border-[#dadada] tw:text-[#8d8d8d] tw:hover:bg-[#f9f9f9]"
                          )}
                          aria-label={`Remove vehicle ${index + 1}`}
                        >
                          <CloseIcon width={12} height={12} color={"#C01F12"} />
                        </button>
                      )}
                    </div>
                    {/* <div className="tw:flex"> */}
                    <Select
                      placeholder={t("editBooking.BookingEditPage.vehicleSelection.selectVehicle")}
                      classNamePrefix="react-select-vehicle"
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          height: "50px",
                          borderRadius: "5px",
                          border: isInvalidState ? "1px solid #EB001B" : "1px solid #dadada",
                          backgroundColor: "#ffffff",
                          fontSize: "15px",
                          boxShadow: "none",
                          "&:hover": {
                            border: isInvalidState ? "1px solid #EB001B" : "1px solid #dadada",
                          },
                          fontWeight: "normal",
                        }),
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          paddingLeft: "14px",
                          fontWeight: "normal",
                        }),
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          borderRadius: "5px",
                          marginTop: "4px",
                        }),
                        option: (baseStyles, state) => ({
                          ...baseStyles,
                          fontSize: "14px",
                          backgroundColor: state.isSelected ? "#dbbe78" : state.isFocused ? "#eddfbb" : "#ffffff",
                          color: state.isSelected ? "#ffffff" : "#282828",
                          cursor: "pointer",
                        }),
                        singleValue: (baseStyles) => ({
                          ...baseStyles,
                          fontWeight: "normal",
                        }),
                      }}
                      autoComplete="off"
                      onChange={(option) => handleChangeVehicle(vehicle.id, option?.value || "")}
                      value={formatVehicle(vehicle.selectedVehicle)}
                      options={vehicleOptions}
                      getOptionLabel={(option) => t(`${option.value.replace(/ /g, ".")}`)}
                      components={{
                        DropdownIndicator: (props) => (
                          <components.DropdownIndicator {...props}>
                            <div className="tw:pr-2">
                              <GoTriangleDown color={isInvalidState ? "#EB001B" : "#DBBE78"} size={16} />
                            </div>
                          </components.DropdownIndicator>
                        ),
                        IndicatorSeparator: () => null,
                        Placeholder: (props) => (
                          <components.Placeholder {...props}>
                            <div className="tw:!flex tw:!items-center">
                              <VehicleIcon width={16} height={16} color={isInvalidState ? "#EB001B" : "#DBBE78"} />
                              <span className="tw:!ml-2">{props.children}</span>
                            </div>
                          </components.Placeholder>
                        ),
                        SingleValue: (props) => (
                          <components.SingleValue {...props}>
                            <div className="tw:!flex tw:!items-center">
                              <VehicleIcon width={16} height={16} color={isInvalidState ? "#EB001B" : "#DBBE78"} />
                              <span className="tw:!ml-2">
                                {t(`${props.data.value.replace(/ /g, ".")}`)}
                              </span>
                            </div>
                          </components.SingleValue>
                        ),
                      }}
                    />
                    {/* </div> */}
                  </div>

                  {renderVehicleCard(vehicle.selectedVehicle)}
                </div>
              ))}
            </div>

            <div>
              <button type="button" onClick={handleAddVehicle}>
                <div className={`tw:flex tw:items-center tw:justify-center tw:gap-x-[10px] tw:h-[45px] tw:rounded-[5px] tw:border tw:border-[#282828] tw:bg-white tw:px-5 ${getTypographyClass(14)}`}>
                  <BsPlusCircle size={16} color="#282828" />
                  <span className="tw:text-[#282828]">{t("editBooking.BookingEditPage.vehicleSelection.addVehicle")}</span>
                </div>
              </button>
            </div>
          </>
        )}

      </div>
    </section>
  );
});

export default VehicleSelectionSection;
